import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone,
  Eye,
  ExternalLink,
  Users,
  DollarSign,
  Target,
  Loader2,
  Copy,
  Check,
  FileText,
  MessageCircle,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Button, Input } from "@/components/kit";

const META_PIXEL_ID = "993045030025532";

function rupiah(n) {
  return "Rp" + Number(n).toLocaleString("id-ID");
}

function AdVariationTab({ varId, name, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition whitespace-nowrap ${
        active
          ? "bg-rose-600 text-white shadow"
          : "bg-rose-50 text-rose-700 hover:bg-rose-100"
      }`}
    >
      {name}
    </button>
  );
}

function DescriptionCard({ description, copied, setCopied, slug, varId }) {
  const copyKey = `desc-${slug}-${varId}`;
  return (
    <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide flex items-center gap-1">
          <MessageCircle className="w-3.5 h-3.5" />
          Primary Text — Meta Ads
        </p>
        <button
          onClick={() => {
            navigator.clipboard.writeText(description);
            setCopied(copyKey);
            setTimeout(() => setCopied(null), 2000);
          }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 bg-sky-100 px-3 py-1.5 rounded-lg hover:bg-sky-200 transition shrink-0"
        >
          {copied === copyKey ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-500" /> Tersalin!
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> Copy Deskripsi
            </>
          )}
        </button>
      </div>
      <p className="text-sm text-sky-950 leading-relaxed whitespace-pre-wrap">
        {description}
      </p>
    </div>
  );
}

function ImagePromptList({ slug, varId, prompts, copied, setCopied }) {
  if (!prompts || !prompts.length) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="mb-3">
        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1">
          🎨 ChatGPT Image Prompts — Ref Carousel Style
        </p>
        <p className="text-xs text-amber-800/80 mt-1">
          Copy satu per satu. Tiap prompt menghasilkan 1 slide terpisah, bukan
          kolase.
        </p>
      </div>
      <div className="space-y-3">
        {prompts.map((item, index) => {
          const copyKey = `img-${slug}-${varId}-${index}`;
          return (
            <div
              key={copyKey}
              className="bg-white rounded-lg px-3 py-3 border border-amber-100"
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                  {item.title || `Slide ${index + 1}`}
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(item.prompt);
                    setCopied(copyKey);
                    setTimeout(() => setCopied(null), 2000);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-lg hover:bg-amber-200 transition shrink-0"
                >
                  {copied === copyKey ? (
                    <>
                      <Check className="w-3 h-3" /> Tersalin!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" /> Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-amber-950 leading-relaxed whitespace-pre-wrap">
                {item.prompt}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminAds() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [selectedVar, setSelectedVar] = useState({}); // { slug: varId }

  const load = () => {
    setLoading(true);
    api
      .get("/admin/ads")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const viewDetail = async (slug) => {
    if (detail?.slug === slug) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/ads/${slug}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/promo/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(slug);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const copyPixelSnippet = (slug) => {
    // Base Meta Pixel sudah terpasang global di index.html (ID: 993045030025532)
    // Snippet ini hanya untuk override / manual install di landing page eksternal
    const snippet = `<!-- Meta Pixel for ${slug} (RESADA — ID: ${META_PIXEL_ID}) -->
<script>
!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=
n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${META_PIXEL_ID}');
fbq('track', 'PageView');
fbq('track', 'ViewContent', {content_name: '${slug}', content_category: 'promo', value: 89000, currency: 'IDR'});
</script>
<noscript><img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1"
/></noscript>`;
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(`pixel-${slug}`);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const copyPixelId = () => {
    navigator.clipboard.writeText(META_PIXEL_ID).then(() => {
      setCopied("pixel-id");
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-7 h-7 text-sky-500 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-rose-100 grid place-items-center shrink-0">
              <Megaphone className="w-5 h-5 text-rose-600" />
            </span>
            <h1 className="font-display text-3xl font-extrabold">
              Ads Manager
            </h1>
          </span>
          <p className="text-slate-500 mt-1">
            Kelola 5 halaman promo / landing page iklan Meta Ads.
          </p>
        </div>
      </div>

      {/* Meta Pixel Info */}
      <Card className="p-5 border border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500 grid place-items-center shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-slate-900">
              Meta Pixel Aktif
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              Base pixel sudah terpasang global di seluruh halaman RESADA &
              halaman promo. <code className="font-mono">PageView</code>,{" "}
              <code className="font-mono">ViewContent</code>,{" "}
              <code className="font-mono">InitiateCheckout</code>, dan{" "}
              <code className="font-mono">Lead</code> di-fire otomatis dari{" "}
              <code className="font-mono">/promo/:slug</code>.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="inline-flex items-center gap-2 text-sm font-mono font-bold text-violet-700 bg-white border border-violet-200 px-3 py-1.5 rounded-lg">
                ID: {META_PIXEL_ID}
              </span>
              <button
                onClick={copyPixelId}
                data-testid="copy-pixel-id-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 bg-white border border-violet-200 px-3 py-1.5 rounded-lg hover:bg-violet-100 transition"
              >
                {copied === "pixel-id" ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-500" /> Tersalin!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy Pixel ID
                  </>
                )}
              </button>
              <a
                href={`https://business.facebook.com/events_manager2/list/pixel/${META_PIXEL_ID}/overview`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 bg-white border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Events Manager
              </a>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="w-10 h-10 rounded-xl bg-rose-50 grid place-items-center">
              <Megaphone className="w-5 h-5 text-rose-600" />
            </div>
            <p className="font-display text-3xl font-extrabold mt-3">
              {data.pages?.length || 0}
            </p>
            <p className="text-sm text-slate-500">Halaman Aktif</p>
          </Card>
          <Card className="p-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="font-display text-3xl font-extrabold mt-3">
              {data.total_conversions || 0}
            </p>
            <p className="text-sm text-slate-500">Total Konversi</p>
          </Card>
          <Card className="p-5">
            <div className="w-10 h-10 rounded-xl bg-sky-50 grid place-items-center">
              <DollarSign className="w-5 h-5 text-sky-600" />
            </div>
            <p className="font-display text-3xl font-extrabold mt-3">
              {rupiah(data.total_revenue || 0)}
            </p>
            <p className="text-sm text-slate-500">Total Revenue</p>
          </Card>
          <Card className="p-5">
            <div className="w-10 h-10 rounded-xl bg-violet-50 grid place-items-center">
              <Target className="w-5 h-5 text-violet-600" />
            </div>
            <p className="font-display text-3xl font-extrabold mt-3">
              {rupiah(89000)}
            </p>
            <p className="text-sm text-slate-500">Harga Promo</p>
          </Card>
        </div>
      )}

      {/* Ad Pages */}
      <div className="space-y-6">
        {data?.pages?.map((ad) => {
          const variations = ad.variations || [];
          const currentVarId = selectedVar[ad.slug] || variations[0]?.id || "existing";
          const currentVar = variations.find((v) => v.id === currentVarId) || variations[0] || {};

          return (
            <Card key={ad.slug} className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 grid place-items-center shrink-0">
                  <Megaphone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-slate-900">
                      {ad.meta_title}
                    </h3>
                    <Badge color="green">Live</Badge>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    /promo/{ad.slug}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                      <Users className="w-3.5 h-3.5" /> {ad.conversions} konversi
                    </span>
                    <span className="flex items-center gap-1 text-sky-600 font-semibold">
                      <DollarSign className="w-3.5 h-3.5" /> {rupiah(ad.revenue)}
                    </span>
                  </div>

                  {/* Variation Tabs */}
                  {variations.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" /> Variasi Iklan
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {variations.map((v) => (
                          <AdVariationTab
                            key={v.id}
                            varId={v.id}
                            name={v.name}
                            active={currentVarId === v.id}
                            onClick={() =>
                              setSelectedVar((prev) => ({ ...prev, [ad.slug]: v.id }))
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description - Primary Text */}
                  {currentVar.description && (
                    <div className="mt-4">
                      <DescriptionCard
                        description={currentVar.description}
                        copied={copied}
                        setCopied={setCopied}
                        slug={ad.slug}
                        varId={currentVarId}
                      />
                    </div>
                  )}

                  {/* Targeting */}
                  <div className="mt-4 bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide flex items-center gap-1">
                      <Target className="w-3.5 h-3.5" /> Rekomendasi Targeting
                      Meta Ads
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-xs text-slate-400">Audience</span>
                        <p className="text-slate-700 font-medium">
                          {ad.targeting.audience}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">Interests</span>
                        <p className="text-slate-700 font-medium">
                          {ad.targeting.interests.join(", ")}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">Locations</span>
                        <p className="text-slate-700 font-medium">
                          {Array.isArray(ad.targeting.locations)
                            ? ad.targeting.locations.join(", ")
                            : ad.targeting.locations}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">Age Range</span>
                        <p className="text-slate-700 font-medium">
                          {ad.targeting.age_range}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">
                          Budget Suggestion
                        </span>
                        <p className="text-slate-700 font-semibold text-emerald-600">
                          {ad.targeting.budget_suggestion}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">Placements</span>
                        <p className="text-slate-700 font-medium">
                          {ad.targeting.placements.join(", ")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Image Prompts for selected variation */}
                  {currentVar.image_prompts && currentVar.image_prompts.length > 0 && (
                    <div className="mt-4">
                      <ImagePromptList
                        slug={ad.slug}
                        varId={currentVarId}
                        prompts={currentVar.image_prompts}
                        copied={copied}
                        setCopied={setCopied}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <a
                      href={`/promo/${ad.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 bg-sky-50 px-3 py-1.5 rounded-lg hover:bg-sky-100 transition"
                    >
                      <Eye className="w-3.5 h-3.5" /> Preview
                    </a>
                    <button
                      onClick={() => copyUrl(ad.slug)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition"
                    >
                      {copied === ad.slug ? (
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      {copied === ad.slug ? "Tersalin!" : "Copy URL"}
                    </button>
                    <button
                      onClick={() => copyPixelSnippet(ad.slug)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 bg-violet-50 px-3 py-1.5 rounded-lg hover:bg-violet-100 transition"
                    >
                      {copied === `pixel-${ad.slug}` ? (
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Target className="w-3.5 h-3.5" />
                      )}
                      {copied === `pixel-${ad.slug}`
                        ? "Pixel Tersalin!"
                        : "Copy Pixel Snippet"}
                    </button>
                    <a
                      href="https://adsmanager.facebook.com"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Buka Meta Ads
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick Setup Guide */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-100">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-3">
          📋 Quick Setup Guide — Meta Ads
        </h3>
        <ol className="space-y-3 text-sm text-slate-700 list-decimal pl-5">
          <li>
            Buka{" "}
            <a
              href="https://adsmanager.facebook.com"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline font-semibold"
            >
              Meta Ads Manager
            </a>
          </li>
          <li>
            Klik <b>Create → Sales Campaign</b>
          </li>
          <li>
            Targeting: gunakan rekomendasi di atas (audience, interests,
            locations)
          </li>
          <li>
            Placement: <b>Automatic (recommended)</b> atau manual sesuai
            rekomendasi
          </li>
          <li>
            Budget: mulai dari{" "}
            <b>
              {data?.pages?.[0]?.targeting?.budget_suggestion ||
                "Rp150.000/hari"}
            </b>
          </li>
          <li>
            Ad creative: gunakan screenshot dari halaman promo atau buat video
            15-30 detik
          </li>
          <li>Destination URL: copy dari tombol "Copy URL" di atas</li>
          <li>
            Pixel:{" "}
            <b>
              sudah otomatis aktif (ID:{" "}
              <span className="font-mono">{META_PIXEL_ID}</span>)
            </b>{" "}
            — base PageView dipasang global, sedangkan{" "}
            <code className="font-mono">ViewContent</code>,{" "}
            <code className="font-mono">InitiateCheckout</code>, &{" "}
            <code className="font-mono">Lead</code> di-fire otomatis dari
            halaman <code className="font-mono">/promo/:slug</code>. Verifikasi
            via Meta Pixel Helper extension.
          </li>
          <li>
            Monitor konversi dari dashboard ini dan optimasi budget per 3 hari
          </li>
        </ol>
      </Card>
    </div>
  );
}
